import React, { Fragment, useState } from 'react';
import { Link } from 'react-scroll';
import { FormattedMessage } from 'react-intl';
import { always } from 'ramda';

import { Image } from '@definitions/artworks.types';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { addLeadingZero } from '@utils/helpers';
import {
  ARTISTS_SCROLL_LINK,
  DETAILS_SCROLL_LINK,
  IMAGE_SCROLL_LINK,
  SCROLL_DURATION,
} from '@components/PDP/pdp.constants';
import { COLORS } from '@theme/colors';
import { ScrollLinkContainer } from '@components/PDP/pdp.styles';

import { Container, Counter, HiddenLinks } from './artworkSidebarLinks.styles';

export interface ArtworkSidebarLinksProps {
  images: Image[];
  setBackgroundColor: (color: COLORS) => void;
}

export const ArtworkSidebarLinks = ({ images, setBackgroundColor }: ArtworkSidebarLinksProps) => {
  const [activeImage, setActiveImage] = useState(0);
  const imageLength = images.length;
  const isLastImage = activeImage + 1 === imageLength;
  const nextImage = isLastImage ? 0 : activeImage + 1;

  const handleSetActiveImage = (index: number) => {
    setActiveImage(index);
    setBackgroundColor(COLORS.WHITE);
  };
  const handleSetActiveArtwork = () => {
    setActiveImage(-1);
    setBackgroundColor(COLORS.GALLERY);
  };

  const renderNextCopy = renderWhenTrueOtherwise(
    always(<FormattedMessage id="artworkSidebarLinks.next" defaultMessage="Next" />),
    always(<FormattedMessage id="artworkSidebarLinks.first" defaultMessage="First" />)
  );
  const renderCounterImages = renderWhenTrueOtherwise(
    always(
      <Fragment>
        <FormattedMessage id="artworkSidebarLinks.image" defaultMessage="Image" />:
        <Counter>
          {addLeadingZero(activeImage + 1)}/{addLeadingZero(imageLength)}
        </Counter>
        {renderNextCopy(!isLastImage)}
      </Fragment>
    ),
    always(<FormattedMessage id="artworkSidebarLinks.viewImages" defaultMessage="View Images" />)
  );
  const renderImageLink = renderWhenTrue(
    always(
      <ScrollLinkContainer>
        <Link
          activeClass="active"
          to={`artwork-image-${nextImage}`}
          href={`#artwork-image-${nextImage}`}
          smooth
          duration={500}
        >
          {renderCounterImages(activeImage !== -1)}
        </Link>
      </ScrollLinkContainer>
    )
  );

  return (
    <Container>
      <HiddenLinks>
        {images.map((image, index) => (
          <Link
            key={index}
            href={`#${IMAGE_SCROLL_LINK}-${index}`}
            to={`${IMAGE_SCROLL_LINK}-${index}`}
            spy
            smooth
            duration={SCROLL_DURATION}
            onSetActive={() => handleSetActiveImage(index)}
          />
        ))}
      </HiddenLinks>
      {renderImageLink(!!imageLength)}
      <ScrollLinkContainer>
        <Link
          to={DETAILS_SCROLL_LINK}
          href={`#${DETAILS_SCROLL_LINK}`}
          spy
          smooth
          duration={SCROLL_DURATION}
          onSetActive={handleSetActiveArtwork}
        >
          <FormattedMessage id="artworkSidebarLinks.artworkDetails" defaultMessage="About the artwork" />
        </Link>
      </ScrollLinkContainer>
      <ScrollLinkContainer>
        <Link
          to={ARTISTS_SCROLL_LINK}
          href={`#${ARTISTS_SCROLL_LINK}`}
          spy
          smooth
          duration={SCROLL_DURATION}
          onSetActive={() => setActiveImage(-1)}
        >
          <FormattedMessage id="artworkSidebarLinks.artworkArtist" defaultMessage="About the artist" />
        </Link>
      </ScrollLinkContainer>
    </Container>
  );
};
